/* eslint-disable max-lines-per-function, max-params */
import { isSafari } from "common/utils/browser";

const initScroll = ({
  parent: parentElement,
  list: listSelector,
  grid: gridSelector,
  arrowLeft: arrowLeftSelector,
  arrowRight: arrowRightSelector,
  loadMoreFn,
  hiddenClass = "hidden"
}) => {
  const parent = parentElement || document;
  const arrowHidden = "hidden";
  const lists = parent.querySelectorAll(listSelector);
  const isGridOverflowing = grid => grid.scrollWidth > grid.clientWidth;
  let isXLDesktop = window.innerWidth >= 1280;
  let hasAnimation = false;

  const scroll = (grid, left, callback) => {
    if (hasAnimation) return;
    hasAnimation = true;

    // This is a workaround for Safari smooth scroll issue
    // https://developer.apple.com/forums/thread/703294
    // Once the issue is fixed, please, revert this commit
    // JIRA LINK: https://dekeo.atlassian.net/browse/DIAMOND-300
    const computedGridStyle = window.getComputedStyle(grid);
    let safariScrollFixApplied = false;

    if (isSafari() && computedGridStyle.overflowX === "hidden") {
      safariScrollFixApplied = true;
      grid.style.overflowX = "auto";
    }

    window.requestAnimationFrame(() => {
      grid.scroll({ left, behavior: "smooth" });
    });

    setTimeout(() => {
      if (isSafari() && safariScrollFixApplied) grid.style.overflowX = "hidden";
      hasAnimation = false;
      callback();
    }, 600);
  };

  const controlsVisibility = async (grid, left, leftArrow, rightArrow) => {
    const leftHidden = left <= 0 || !isGridOverflowing(grid);
    let rightHidden = left + grid.clientWidth >= grid.scrollWidth || !isGridOverflowing(grid);
    if (loadMoreFn instanceof Function && rightHidden) {
      await loadMoreFn(grid);
      rightHidden = left + grid.clientWidth >= grid.scrollWidth || !isGridOverflowing(grid);
    }
    if (!leftHidden || !rightHidden) {
      leftArrow.classList.remove(arrowHidden);
      rightArrow.classList.remove(arrowHidden);
    }

    const arrowHiddenClass = leftHidden && rightHidden ? arrowHidden : hiddenClass;
    leftArrow.classList.toggle(arrowHiddenClass, leftHidden);
    rightArrow.classList.toggle(arrowHiddenClass, rightHidden);
  };

  const getScrollRightPosition = grid => {
    const children = Array.from(grid.childNodes).filter(child => child.childNodes.length > 0);
    let left = grid.scrollLeft + grid.clientWidth;
    let prevChildLeft = 0;

    for (let i = 0; i < children.length; i += 1) {
      const child = children[i];
      const childLeft = child.offsetLeft + child.clientWidth;
      if (left < childLeft) {
        left = prevChildLeft;

        // Avoid scrolling a half of card on the last scroll
        if (left + grid.clientWidth + child.clientWidth > grid.scrollWidth) {
          left = grid.scrollWidth;
        }
        break;
      }
      prevChildLeft = childLeft;
    }
    return left;
  };

  const getScrollLeftPosition = grid => {
    const children = Array.from(grid.childNodes).filter(child => child.childNodes.length > 0);
    let left = grid.scrollLeft - grid.clientWidth;
    let nextChildLeft = grid.scrollWidth;

    for (let i = children.length - 1; i >= 0; i -= 1) {
      const child = children[i];
      const childLeft = child.offsetLeft - grid.clientWidth;
      if (left > childLeft) {
        left = nextChildLeft;

        // Avoid scrolling a half of card on the last scroll
        if (left < child.clientWidth) {
          left = 0;
        }
        break;
      }
      nextChildLeft = childLeft;
    }
    return left;
  };

  const scrollLeft = (grid, leftArrow, rightArrow) => {
    const left = getScrollLeftPosition(grid);
    scroll(grid, left, () => {
      controlsVisibility(grid, left, leftArrow, rightArrow);
    });
  };

  const scrollRight = (grid, leftArrow, rightArrow) => {
    const left = getScrollRightPosition(grid);
    scroll(grid, left, () => {
      controlsVisibility(grid, left, leftArrow, rightArrow);
    });
  };

  const reset = () => {
    lists.forEach(list => {
      const grid = list.querySelector(gridSelector);
      const leftArrow = list.querySelector(arrowLeftSelector);
      const rightArrow = list.querySelector(arrowRightSelector);

      grid.scroll({ left: 0 });

      controlsVisibility(grid, 0, leftArrow, rightArrow);
    });
  };

  lists.forEach(list => {
    if (list.classList.contains("initialized")) {
      return;
    }
    const grid = list.querySelector(gridSelector);
    const leftArrow = list.querySelector(arrowLeftSelector);
    const rightArrow = list.querySelector(arrowRightSelector);

    leftArrow.addEventListener("click", () => scrollLeft(grid, leftArrow, rightArrow));
    rightArrow.addEventListener("click", () => scrollRight(grid, leftArrow, rightArrow));

    grid.addEventListener("scroll", () => {
      controlsVisibility(grid, grid.scrollLeft, leftArrow, rightArrow);
    });

    list.classList.add("initialized");
  });
  reset();

  // If we go trough breakpoint - reset the scroll to zero
  window.addEventListener("resize", () => {
    if (window.innerWidth < 992) return;
    if (window.innerWidth >= 1280 && !isXLDesktop) {
      reset();
      isXLDesktop = true;
    }

    if (window.innerWidth < 1280 && isXLDesktop) {
      reset();
      isXLDesktop = false;
    }
  });

  return { reset };
};

export default initScroll;
